import createOpenApiFetchClient, {
  type Client,
  type FetchOptions,
  type FetchResponse,
} from 'openapi-fetch';
import type { ApiEnvelope, ApiMeta } from '../envelope';
import { ApiClientError, TransportError } from '../errors/index';
import { createMiddleware, type HttpCoreOptions } from './middleware';

/**
 * Build a typed client wrapping openapi-fetch per RFC-001 §4.3 / architect H1.
 *
 * `request` is path-first: the caller writes `request('post', '/orders', {...})`
 * and `Path` is constrained to paths that actually define `Method`. This
 * mirrors openapi-fetch's `ClientRequestMethod` so the (Method × Path × Init)
 * narrowing is exact — no `as never` / `as ApiRequest` escape hatch needed.
 *
 * Returns `[data, meta]` after envelope unwrap; openapi-fetch's middleware
 * cannot strip the envelope (it operates on the raw `Response`).
 *
 * Usage:
 * ```ts
 * import type { paths } from '@everline/api-public';
 * const client = createTypedClient<paths>({ baseUrl: '/api/v1', channel: 'WEB', ... });
 * const [data, meta] = await client.request('post', '/orders', { body: { ... } });
 * //      ^ typed as components['schemas']['OrderCreatedResponse']
 * ```
 */
export interface CreateTypedClientOptions extends HttpCoreOptions {
  baseUrl: string;
  /** Optional custom fetch (e.g., for Next.js server-side cookie propagation). */
  fetch?: typeof fetch;
}

/** Lowercase HTTP verb literals openapi-fetch dispatches on. */
type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'options' | 'head' | 'patch' | 'trace';

/** Paths in `Paths` that define `Method`. */
type PathsWithMethod<Paths, Method extends HttpMethod> = {
  [Pathname in keyof Paths]: Paths[Pathname] extends { [K in Method]: unknown }
    ? Pathname
    : never;
}[keyof Paths];

/** Operation object for a (Path, Method) pair. */
type OperationFor<
  Paths,
  Path extends keyof Paths,
  Method extends HttpMethod,
> = Paths[Path] extends { [K in Method]: infer Op } ? Op : never;

/**
 * Headers the http-core middleware injects automatically. Callers should not
 * be forced to provide them — the schema may mark them required, but at the
 * wrapper layer they become optional.
 */
type MiddlewareInjectedHeaderKey =
  | 'Idempotency-Key'
  | 'X-Trace-Id'
  | 'X-Channel'
  | 'Accept-Language';

/**
 * Loosens an operation's `parameters` so that middleware-injected headers are
 * optional from the caller's perspective. We strip the listed keys from any
 * required-header shape and re-add them as optional. Other params (path,
 * query, body) are preserved unchanged.
 */
type LoosenOp<Op> = Op extends { parameters: infer P }
  ? P extends { header: infer H }
    ? Omit<Op, 'parameters'> & {
        parameters: Omit<P, 'header'> & {
          header?: Omit<H, MiddlewareInjectedHeaderKey> & {
            [K in MiddlewareInjectedHeaderKey]?: K extends keyof H ? H[K] : string;
          };
        };
      }
    : Op
  : Op;

/**
 * The success `data` field from openapi-fetch is the parsed JSON body, which
 * for everline APIs is the ApiEnvelope (`{ success, data, error, meta }`).
 * Unwrap one more level to expose the inner `data` payload to callers — this
 * mirrors the runtime envelope-unwrap done in `request` below.
 */
type SuccessDataOf<Op, Init> = Extract<
  FetchResponse<Op extends Record<string | number, unknown> ? Op : never, Init, `${string}/${string}`>,
  { data: unknown }
>['data'] extends infer Envelope
  ? Envelope extends { data: infer Inner }
    ? Inner
    : Envelope
  : never;

export interface TypedClient<Paths extends object> {
  raw: Client<Paths>;
  /**
   * Type-safe fetch. Returns `[data, meta]` on 2xx, throws `ApiClientError` on
   * envelope error, throws `TransportError` on network failure.
   */
  request: <
    Method extends HttpMethod,
    Path extends PathsWithMethod<Paths, Method>,
  >(
    method: Method,
    path: Path,
    init?: FetchOptions<LoosenOp<OperationFor<Paths, Path, Method>>>,
  ) => Promise<
    readonly [
      SuccessDataOf<
        OperationFor<Paths, Path, Method>,
        FetchOptions<LoosenOp<OperationFor<Paths, Path, Method>>>
      >,
      ApiMeta,
    ]
  >;
}

export const createTypedClient = <Paths extends object>(
  opts: CreateTypedClientOptions,
): TypedClient<Paths> => {
  const clientOpts: Parameters<typeof createOpenApiFetchClient<Paths>>[0] = {
    baseUrl: opts.baseUrl,
  };
  if (opts.fetch) clientOpts.fetch = opts.fetch as never;
  const raw = createOpenApiFetchClient<Paths>(clientOpts);
  raw.use(createMiddleware(opts));

  const request = (async (method: string, path: string, init?: unknown) => {
    let result: { data?: unknown; error?: unknown; response: Response };
    try {
      // `raw.request` is the unified per-method dispatcher exposed by
      // openapi-fetch. Type-erased internally because the public wrapper
      // signature above already enforces the (Method × Path × Init) constraint
      // — the internal call goes through a verified narrow type at the seam.
      result = await (
        raw.request as (
          m: string,
          p: string,
          i?: unknown,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )(method, path, init);
    } catch (cause) {
      throw new TransportError(`Network failure on ${method} ${path}`, cause);
    }

    const status = result.response.status;
    const env = (result.data ?? result.error) as ApiEnvelope<unknown> | undefined;

    if (!env || typeof env !== 'object' || !('success' in env)) {
      // BE contract violation: not an envelope. Treat as transport.
      throw new TransportError(
        `Non-envelope response on ${method} ${path} (status ${status})`,
        result,
      );
    }

    if (env.success === true) {
      return [env.data, env.meta] as const;
    }

    throw new ApiClientError(env.error, { status, meta: env.meta });
  }) as TypedClient<Paths>['request'];

  return { raw, request };
};
